import type {
	IJobContract,
	IJobDocuments,
	IJobInvoice,
	IJobLabor,
	IJobMaterial,
	IJobOpenAR,
	IJobOther,
	IJobPayments,
	IJobProbability,
	IJobRole,
	IJobStatus,
	IJobSubContract,
	IJobTimeline,
	IJobTotal,
	IMilestone,
	ISubJob,
	ISubjobContractState,
	ISubJobJCR,
	ISubjobProductionState,
	ISubJobSCR,
	// ISubJobIndex,
	// IJobResources,
	ISubjobType
} from '@/interfaces'
import { Collection, SubJob, SubJobIndex, SubJobSearchResult, SubjobStage } from '@/models'
import { BaseService, type IAuthService } from '@/services'
import { APIEndpoints, APIScopes, APIURL } from '@/services/API'
export interface ISubJobID {
	SubJobID: string
}

export class SubJobService extends BaseService {
	constructor(authService: IAuthService) {
		super(APIURL, authService, APIScopes)
	}

	async getIndex(params?: object): Promise<SubJobIndex[]> {
		return this.getData<SubJobIndex[]>(`${APIEndpoints.subJob}/`, params)
			.then((data) => {
				return data.map((c: SubJobIndex) => new SubJobIndex(c))
			})
			.catch((err) => {
				throw err
			})
	}

	async get(id: string): Promise<SubJob> {
		return this.getData<SubJob>(`${APIEndpoints.subJob}/${id}`)
			.then((data) => {
				return new SubJob(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async getJCR(id: string): Promise<ISubJobJCR[]> {
		return this.getData<ISubJobJCR[]>(`${APIEndpoints.subJob}/${id}/jcr`)
			.then((data) => {
				return data as ISubJobJCR[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getSCR(id: string): Promise<ISubJobSCR> {
		return this.getData<ISubJobSCR[]>(`${APIEndpoints.subJob}/${id}/scr`)
			.then((data) => {
				return data[0] as ISubJobSCR
			})
			.catch((err) => {
				throw err
			})
	}

	async add(subJob: SubJob): Promise<ISubJob> {
		const obj = subJob
		return this.postData<SubJob, ISubJob>(`${APIEndpoints.subJob}/add`, obj)
			.then((res) => {
				return res as ISubJob
			})
			.catch((err) => {
				throw err
			})
	}

	async update(subJob: SubJob): Promise<SubJob> {
		const obj = subJob
		return this.patchData(`${APIEndpoints.subJob}/update`, obj)
			.then((data) => {
				return new SubJob(data)
			})
			.catch((err) => {
				throw err
			})
	}

	async getRoles(): Promise<IJobRole[]> {
		return this.getData<IJobRole[]>(`${APIEndpoints.subJob}/roles`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getMilestones(): Promise<IMilestone[]> {
		return this.getData<IMilestone[]>(`${APIEndpoints.subJob}/milestones`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getResources(): Promise<string[]> {
		return this.getData<string[]>(`${APIEndpoints.subJob}/resources`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getTimelines(): Promise<IJobTimeline[]> {
		return this.getData<IJobTimeline[]>(`${APIEndpoints.subJob}/timeline`)
			.then((data) => {
				return data as IJobTimeline[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getProbabilities(): Promise<IJobProbability[]> {
		return this.getData<IJobProbability[]>(`${APIEndpoints.subJob}/probability`)
			.then((data) => {
				return data as IJobProbability[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubjobContractStates(): Promise<ISubjobContractState[]> {
		return this.getData<ISubjobContractState[]>(`${APIEndpoints.subJob}/states-contracting`)
			.then((data) => {
				return data as ISubjobContractState[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubjobProductionStates(): Promise<ISubjobProductionState[]> {
		return this.getData<ISubjobProductionState[]>(`${APIEndpoints.subJob}/states-production`)
			.then((data) => {
				return data as ISubjobProductionState[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getTypes(): Promise<ISubjobType[]> {
		return this.getData<ISubjobType[]>(`${APIEndpoints.subJob}/types`)
			.then((data) => {
				return data as ISubjobType[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getStatuses(): Promise<IJobStatus[]> {
		return this.getData<IJobStatus[]>(`${APIEndpoints.subJob}/statuses`)
			.then((data) => {
				return data as IJobStatus[]
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobContract(id: string): Promise<IJobContract[]> {
		return this.getData<IJobContract[]>(`${APIEndpoints.subJob}/summary/${id}/contract`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobInvoice(id: string): Promise<IJobInvoice[]> {
		return this.getData<IJobInvoice[]>(`${APIEndpoints.subJob}/summary/${id}/invoice`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobOpenAR(id: string): Promise<IJobOpenAR[]> {
		return this.getData<IJobOpenAR[]>(`${APIEndpoints.subJob}/summary/${id}/open-ar`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobPayments(id: string): Promise<IJobPayments[]> {
		return this.getData<IJobPayments[]>(`${APIEndpoints.subJob}/summary/${id}/payments`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobLabor(id: string): Promise<IJobLabor[]> {
		return this.getData<IJobLabor[]>(`${APIEndpoints.subJob}/summary/${id}/labor`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobLaborDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.subJob}/summary/${id}/labor/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobMaterial(id: string): Promise<IJobMaterial[]> {
		return this.getData<IJobMaterial[]>(`${APIEndpoints.subJob}/summary/${id}/materials`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobMaterialDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.subJob}/summary/${id}/material/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobOther(id: string): Promise<IJobOther[]> {
		return this.getData<IJobOther[]>(`${APIEndpoints.subJob}/summary/${id}/other`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobOtherDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.subJob}/summary/${id}/other/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobSubContract(id: string): Promise<IJobSubContract[]> {
		return this.getData<IJobSubContract[]>(`${APIEndpoints.subJob}/summary/${id}/subcontract`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobSubContractDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.subJob}/summary/${id}/subcontract/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobTotal(id: string): Promise<IJobTotal[]> {
		return this.getData<IJobTotal[]>(`${APIEndpoints.subJob}/summary/${id}/total`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobTotalDetails(id: string): Promise<string> {
		const config = { responseType: 'text' }
		return this.getData<string>(`${APIEndpoints.subJob}/summary/${id}/total/detail/csv`, {}, config)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubJobDocuments(id: string): Promise<IJobDocuments[]> {
		return this.getData<IJobDocuments[]>(`${APIEndpoints.subJob}/${id}/documents`)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async subjobExpressSave(subJob: SubJob): Promise<SubJob> {
		return this.patchData(`${APIEndpoints.subJob}/${subJob.id}/express`, subJob)
			.then((data) => {
				return data
			})
			.catch((err) => {
				throw err
			})
	}

	async searchSubJobs(query: string): Promise<SubJobSearchResult[]> {
		const params = { q: query, limit: 100 }
		return this.getData<SubJobSearchResult[]>(`${APIEndpoints.subJob}/fuzzy-search`, params)
			.then((data) => {
				return data.map((s: SubJobSearchResult) => new SubJobSearchResult(s))
			})
			.catch((err) => {
				throw err
			})
	}

	async getSubjobStages(subjobId: number): Promise<Collection<SubjobStage>> {
		return this.getData<SubjobStage[]>(`${APIEndpoints.subJob}/${subjobId}/stages`)
			.then((res) => {
				return new Collection(SubjobStage, res)
			})
			.catch((err) => {
				throw err
			})
	}
}
